/**
 * Date helpers for the business server. All day logic is Asia/Jerusalem and every day value is a
 * `YYYY-MM-DD` string compared lexicographically (ARCHITECTURE §3).
 *
 * `shared/kit` does not ship a `dates.ts` yet, so this module is the local implementation (character
 * for character the personal server's — the two systems share nothing at runtime, §0.1) and the only
 * place in `business/server` that constructs a `Date` for "now". Metrics never call `todayIL()` —
 * they take `asOf` explicitly so golden tests are deterministic.
 */

export const IL_TZ = 'Asia/Jerusalem';

const IL_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: IL_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

/** Current instant (injectable in tests through the callers, never inside a metric). */
export function nowIL(): Date {
  return new Date();
}

/** The Israel calendar day of an instant (default: now). */
export function todayIL(at: Date = new Date()): string {
  return IL_DAY.format(at);
}

/** The Israel calendar day of an ISO timestamp; null for null/unparsable input. */
export function dayOfIso(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return IL_DAY.format(new Date(t));
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True for a syntactically valid and real `YYYY-MM-DD` day. */
export function isDay(v: unknown): boolean {
  if (typeof v !== 'string' || !DAY_RE.test(v)) return false;
  const y = Number(v.slice(0, 4));
  const m = Number(v.slice(5, 7));
  const d = Number(v.slice(8, 10));
  return m >= 1 && m <= 12 && d >= 1 && d <= daysInMonthOf(y, m);
}

const MONTH_RE = /^\d{4}-\d{2}$/;

export function isMonthString(v: unknown): boolean {
  if (typeof v !== 'string' || !MONTH_RE.test(v)) return false;
  const m = Number(v.slice(5, 7));
  return m >= 1 && m <= 12;
}

function utc(day: string): number {
  return Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
}

function fromUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Add `n` days (pure UTC arithmetic — never `new Date(dayString)` in local time). */
export function addDays(day: string, n: number): string {
  return fromUtc(utc(day) + n * 86_400_000);
}

/** Whole days from `a` to `b` (b − a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((utc(b) - utc(a)) / 86_400_000);
}

/** `YYYY-MM` of a day. */
export function monthOf(day: string): string {
  return day.slice(0, 7);
}

export function daysInMonthOf(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

/** Number of days in the calendar month of `YYYY-MM` or `YYYY-MM-DD`. */
export function daysInMonth(monthOrDay: string): number {
  return daysInMonthOf(Number(monthOrDay.slice(0, 4)), Number(monthOrDay.slice(5, 7)));
}

/** First day of a `YYYY-MM`. */
export function monthStart(month: string): string {
  return `${month.slice(0, 7)}-01`;
}

/** Last day of a `YYYY-MM`. */
export function monthEnd(month: string): string {
  const m = month.slice(0, 7);
  return `${m}-${String(daysInMonth(m)).padStart(2, '0')}`;
}

/** Shift a `YYYY-MM` by `n` months. */
export function addMonths(month: string, n: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = total - ny * 12 + 1;
  return `${String(ny).padStart(4, '0')}-${String(nm).padStart(2, '0')}`;
}

/** Whole months from `a` to `b` (b − a), both `YYYY-MM`. */
export function monthsBetween(a: string, b: string): number {
  return (Number(b.slice(0, 4)) - Number(a.slice(0, 4))) * 12 + (Number(b.slice(5, 7)) - Number(a.slice(5, 7)));
}

/** Ascending list of `YYYY-MM` from `from` to `to`, inclusive. */
export function monthRange(from: string, to: string): string[] {
  const out: string[] = [];
  const n = monthsBetween(from.slice(0, 7), to.slice(0, 7));
  for (let i = 0; i <= n; i++) out.push(addMonths(from.slice(0, 7), i));
  return out;
}

/** The day `dayOfMonth` inside `month`, clamped to the month's length. */
export function dayInMonth(month: string, dayOfMonth: number): string {
  const len = daysInMonth(month);
  const d = Math.min(Math.max(1, Math.trunc(dayOfMonth)), len);
  return `${month.slice(0, 7)}-${String(d).padStart(2, '0')}`;
}

/** 0 = Sunday … 6 = Saturday (UTC arithmetic on the day string; no time zone drift). */
export function weekdayIL(day: string): number {
  return new Date(utc(day)).getUTCDay();
}

/** Friday (5) or Saturday (6). */
export function isWeekendIL(day: string): boolean {
  const w = weekdayIL(day);
  return w === 5 || w === 6;
}

/**
 * Next business day: Fri/Sat (and any day in `holidays`) roll forward to the next Sun–Thu working day
 * (spec-personal §2.3 — a charge that falls on the weekend is taken on the next business day).
 */
export function nextBusinessDay(day: string, holidays: ReadonlySet<string> = new Set()): string {
  let d = day;
  for (let i = 0; i < 10; i++) {
    if (!isWeekendIL(d) && !holidays.has(d)) return d;
    d = addDays(d, 1);
  }
  return d;
}

/** ISO-8601 UTC timestamp of an instant. */
export function isoOf(at: Date = new Date()): string {
  return at.toISOString();
}

/** Clamp a day into `[min, max]` (lexicographic). */
export function clampDay(day: string, min: string, max: string): string {
  return day < min ? min : day > max ? max : day;
}

/** Hours between two instants (ms difference / 3.6e6), or null when either is missing. */
export function hoursBetween(fromIso: string | null | undefined, toMs: number): number | null {
  if (!fromIso) return null;
  const t = Date.parse(fromIso);
  if (!Number.isFinite(t)) return null;
  return (toMs - t) / 3_600_000;
}
